import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import QuoteFormModal from '../components/QuoteFormModal';
import SEO from '../components/SEO';
import { productSchema, breadcrumbSchema } from '../lib/seoConstants';
import { ArrowLeft, ArrowRight, Sparkles, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { t, isRTL, language } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuote, setShowQuote] = useState(false);
  const lang = language === 'ar' ? 'ar' : 'en';
  const dir = isRTL ? 'rtl' : 'ltr';
  const fontFamily = isRTL ? "'Cairo', 'Tajawal', sans-serif" : "'DM Sans', 'Inter', sans-serif";
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  useEffect(() => {
    axios.get(`${API}/api/products/${slug}`)
      .then(r => setProduct(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const handleCTA = () => {
    if (!product) return;
    if (product.cta_type === 'whatsapp') {
      const name = product[`name_${lang}`] || product.name_ar || '';
      const msg = encodeURIComponent(lang === 'ar'
        ? `مرحبًا، أنا مهتم بخدمة: ${name} وأرغب بالحصول على عرض سعر.`
        : `Hello, I'm interested in: ${name} and would like to request a quote.`);
      window.open(`https://api.whatsapp.com/send?phone=966533993007&text=${msg}`, '_blank', 'noopener,noreferrer');
    } else {
      setShowQuote(true);
    }
  };

  if (loading) return (
    <div dir={dir} style={{ fontFamily }}>
      <Header />
      <div className="min-h-screen flex items-center justify-center pt-16"><div className="w-8 h-8 border-2 border-[#00B4D8] border-t-transparent rounded-full animate-spin" /></div>
    </div>
  );

  if (!product) return (
    <div dir={dir} style={{ fontFamily }}>
      <Header />
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <p className="text-slate-400 mb-4">Product not found</p>
          <Link to="/products" className="text-[#00B4D8] text-sm font-medium">{t('products.backToProducts')}</Link>
        </div>
      </div>
    </div>
  );

  const name = product[`name_${lang}`] || product.name_ar || '';
  const shortDesc = product[`short_desc_${lang}`] || product.short_desc_ar || '';
  const detailedDesc = product[`detailed_desc_${lang}`] || product.detailed_desc_ar || '';
  const features = product[`features_${lang}`] || product.features_ar || [];
  const ctaText = product[`cta_text_${lang}`] || product.cta_text_ar || t('products.cta');

  return (
    <div dir={dir} style={{ fontFamily }}>
      <SEO
        path={`/products/${slug}`}
        type="product"
        titleEn={`${product.name_en || product.name_ar} | Nexa Solutions`}
        titleAr={`${product.name_ar || product.name_en} | نيكسا سولوشينز`}
        descriptionEn={product.short_desc_en || product.short_desc_ar}
        descriptionAr={product.short_desc_ar || product.short_desc_en}
        image={product.image ? (product.image.startsWith('http') ? product.image : `${API}/api/files/${product.image}`) : undefined}
        schemas={[
          productSchema(product, lang),
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Products', url: '/products' },
            { name: name, url: `/products/${slug}` },
          ]),
        ]}
      />
      <Header />
      <section className="pt-20 bg-gradient-to-br from-[#0A1628] via-[#0E2240] to-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24">
          <Link to="/products" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors">
            <BackArrow className="w-4 h-4" /> {t('products.backToProducts')}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">{name}</h1>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">{shortDesc}</p>
              <button onClick={handleCTA} data-testid="product-cta-btn"
                className="mt-8 inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0094B3] text-white font-semibold px-8 py-3.5 rounded-lg transition-colors text-sm">
                {ctaText}
              </button>
            </div>
            <div className="aspect-[16/10] bg-white/5 rounded-2xl overflow-hidden">
              {product.image ? (
                <img src={product.image.startsWith('http') ? product.image : `${API}/api/files/${product.image}`} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-14 h-14 text-white/10" /></div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          {detailedDesc && (
            <div className="mb-12">
              <p className="text-base text-slate-600 leading-relaxed whitespace-pre-line">{detailedDesc}</p>
            </div>
          )}

          {features.length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl font-bold text-[#0A1628] mb-6">{t('products.features')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#F8FAFC] border border-slate-100 rounded-lg p-4">
                    <div className="w-6 h-6 bg-[#00B4D8]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#00B4D8]" />
                    </div>
                    <span className="text-sm text-slate-700">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.gallery && product.gallery.length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl font-bold text-[#0A1628] mb-6">{t('products.gallery')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.gallery.map((img, i) => (
                  <div key={i} className="aspect-[16/10] bg-[#F1F5F9] rounded-xl overflow-hidden">
                    <img src={img.startsWith('http') ? img : `${API}/api/files/${img}`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center pt-8 border-t border-slate-100">
            <button onClick={handleCTA} className="inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0094B3] text-white font-semibold px-8 py-3.5 rounded-lg transition-colors text-sm">
              {ctaText}
            </button>
          </div>
        </div>
      </section>

      {showQuote && <QuoteFormModal productName={name} onClose={() => setShowQuote(false)} />}
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
